import {
  HTTP_400_BAD_REQUEST,
  HTTP_401_UNAUTHORIZED,
  HTTP_404_NOT_FOUND,
  HTTP_405_METHOD_NOT_ALLOWED,
  HTTP_500_INTERNAL_SERVER_ERROR,
} from '../../constants/ActionTypes'
import {
  HTTP_400_BAD_REQUEST_CODE,
  HTTP_401_UNAUTHORIZED_CODE,
  HTTP_404_NOT_FOUND_CODE,
  HTTP_405_METHOD_NOT_ALLOWED_CODE,
  HTTP_500_INTERNAL_SERVER_ERROR_CODE
} from "../../constants/HttpStatusCode";
import { push } from 'react-router-redux';
import axios from 'axios';
import qs from 'qs';

//HTTP 400
const badRequest = (error) => ({
  type: HTTP_400_BAD_REQUEST,
  payload: {
    error: error.response
  }
});

//HTTP 401
const unauthorized = (error) => ({
  type: HTTP_401_UNAUTHORIZED,
  payload: {
    error: error.response
  }
});

//HTTP 404
const notFound = (error) => ({
  type: HTTP_404_NOT_FOUND,
  payload: {
    error: error.response
  }
});

//HTTP 405
const methodNotAllowed = (error) => ({
  type: HTTP_405_METHOD_NOT_ALLOWED,
  payload: {
    error: error.response
  }
});

//HTTP 500
const internalServerError = (error) => ({
  type: HTTP_500_INTERNAL_SERVER_ERROR,
  payload: {
    error: error.response
  }
});


// APIリクエストのタイムアウト（ミリ秒）
// nginxのfastcgi_read_timeout(600秒)より必ず短い値にすること。
// nginxが先にタイムアウトすると、nginxが生成する504レスポンスにはCORSヘッダーが付与されないため、
// ブラウザにレスポンスを遮断され、axiosのerrorにresponseが乗らなくなる（エラーハンドリングが破綻する）。
export const DEFAULT_API_TIMEOUT_MS = 300000; // 5分
// 大量データを同期的に処理するAPI用。DEFAULTより長いがnginxの600秒より短い
export const LONG_API_TIMEOUT_MS = 540000; // 9分

/**
 * レスポンスを受け取れなかったエラーに、レスポンス相当のオブジェクトを補完する。
 *
 * タイムアウト・通信断・CORSヘッダーが付かないnginxの504などではerror.responseが存在しない。
 * 各reducerはaction.payload.error.dataやaction.payload.error.statusを直接参照しているため、
 * responseが無いまま渡すとreducerが例外を投げる。するとstateが更新されず、
 * isPosting/isSaving等がtrueのまま固定されてボタンがローディングのまま戻らなくなる。
 *
 * @param {Object} error axiosのエラーオブジェクト
 * @return {Object} responseを補完したエラーオブジェクト
 */
const normalizeErrorResponse = (error) => {
  // axios以外の値（文字列など）がthrowされた場合に備える。
  // プリミティブへのプロパティ代入はstrictモードで例外になり、
  // その例外でrejectに到達できずPromiseが未解決のまま残ってしまう。
  if (!error || typeof error !== 'object') {
    return error;
  }

  // responseが既にある場合（HTTPステータスを受け取れている場合）はそのまま扱う
  if (!error.response) {
    error.response = {
      status: null, // HTTPレスポンスを受け取れていないためnull
      data: {},
      isConnectionError: true,
      isTimeout: error.code === 'ECONNABORTED', // axiosのタイムアウトはECONNABORTED
      isCanceled: axios.isCancel(error), // ログインユーザーの切替でキャンセルされたか
    };
  }

  return error;
};

/**
 * レスポンスを受け取れなかったエラーかどうかを判定する
 *
 * @param {Object} errorResponse action.payload.error（=axiosのerror.response）
 * @return {boolean}
 */
export const isConnectionError = (errorResponse) =>
  !!(errorResponse && errorResponse.isConnectionError);

/**
 * タイムアウトによるエラーかどうかを判定する
 *
 * @param {Object} errorResponse action.payload.error（=axiosのerror.response）
 * @return {boolean}
 */
export const isTimeoutError = (errorResponse) =>
  !!(errorResponse && errorResponse.isTimeout);

// 通信中リクエストのキャンセル関数
const pendingRequestCancelers = new Set();

// ログインセッションの世代。代理ログイン等でログインユーザーが切り替わるたびに進める
let sessionGeneration = 0;

// ログインユーザー切替APIのPromise。切替中以外はnull
let sessionSwitchPromise = null;

// ログインユーザー切替APIを発行中かどうか。切替API自体は切替完了を待たずに送信する
let isIssuingSwitchRequest = false;

/**
 * ログインユーザーの切り替え（子アカウントへの代理ログイン・親アカウントへ戻る）を行う
 *
 * 代理ログインAPIはバックエンドでセッションIDを再生成し、旧セッションを破棄する。
 * 旧セッションIDのCookieのまま他のリクエストが通信していると、401になったり、
 * そのレスポンスのSet-Cookieで新しいセッションのCookieが上書きされてログアウト状態になる。
 * そのため以下を行う。
 * - 切替前に発行されて通信中のリクエストをキャンセルし、その結果ではサインイン画面に遷移させない
 * - 切替中に発行されたリクエストは、切替APIが完了して新しいCookieになるまで送信を待たせる
 *
 * @param {Function} switchRequest 切替APIを呼び出してPromiseを返す関数（callApiを同期的に呼び出すこと。
 *   awaitなどを挟むと切替API自体が切替完了を待ってしまい、完了しなくなる）
 * @return {Promise} 切替APIのPromise
 */
export const runSessionSwitch = (switchRequest) => {
  sessionGeneration += 1;
  pendingRequestCancelers.forEach((cancel) => cancel('session switched'));
  pendingRequestCancelers.clear();

  isIssuingSwitchRequest = true;
  let request;
  try {
    request = switchRequest();
  } finally {
    isIssuingSwitchRequest = false;
  }

  // 切替の成否に関わらず、完了したら切替中を解除する。
  // 呼び出し元のthen/catchより先に解除し、その中で発行されるリクエストを待たせないようにする
  const finishSessionSwitch = () => {
    // 後から別の切替が始まっている場合は、そちらの完了まで切替中のままにする
    if (sessionSwitchPromise === currentSwitchPromise) {
      sessionSwitchPromise = null;
    }
  };
  const switchedRequest = request.then(
    (response) => {
      finishSessionSwitch();
      return response;
    },
    (error) => {
      finishSessionSwitch();
      throw error;
    }
  );
  // 待たせていたリクエストは切替が失敗しても送信する
  const currentSwitchPromise = switchedRequest.catch(() => {});
  sessionSwitchPromise = currentSwitchPromise;

  return switchedRequest;
};

export const callApi = (method, path, data = null, dispatch, isDownload = false, options = null) => {
  // optionsは既定値を上書きするためのもの。指定されなかった項目には既定値が使われる
  let requestOptions = {
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: DEFAULT_API_TIMEOUT_MS,
    paramsSerializer: function (params) {
      return qs.stringify(params, { encode: true, arrayFormat: 'indices' })
    },
    ...(options || {}),
  };

  if (isDownload) {
    requestOptions['responseType'] = 'blob';
  }

  // ログインユーザーの切替時にキャンセルできるようにする
  const cancelSource = axios.CancelToken.source();
  requestOptions.cancelToken = cancelSource.token;
  pendingRequestCancelers.add(cancelSource.cancel);
  // リクエスト発行時点のセッション世代。切替前に発行されたリクエストかの判定に使う
  const requestSessionGeneration = sessionGeneration;
  console.log(path); // eslint-disable-line no-console
  console.log(data); // eslint-disable-line no-console
  console.log(requestOptions); // eslint-disable-line no-console

  const sendRequest = () => {
    switch (method) {
      case 'POST':
        return axios.post(path, data, requestOptions);
      case 'GET': {
        let options = requestOptions;
        options.params = data;
        return axios.get(
          path,
          options
        );
      }
      case 'PUT':
        return axios.put(path, data, requestOptions);
      case 'PATCH':
        return axios.patch(path, data, requestOptions);
      case 'DELETE':
        requestOptions.params = data;
        return axios.delete(path, requestOptions);
      default:
        return undefined;
    }
  };

  // ログインユーザーの切替中は、切替APIが完了して新しいCookieになってから送信する
  const shouldWaitForSessionSwitch = !!sessionSwitchPromise && !isIssuingSwitchRequest;
  const request = shouldWaitForSessionSwitch ? sessionSwitchPromise.then(sendRequest) : sendRequest();
  console.log(request); // eslint-disable-line no-console

  return new Promise((resolve, reject) => {
    request.then((response) => {
      pendingRequestCancelers.delete(cancelSource.cancel);
      console.log(response); // eslint-disable-line no-console
      resolve(response)
    }).catch((error) => {
      pendingRequestCancelers.delete(cancelSource.cancel);
      console.log(error); // eslint-disable-line no-console

      // 正規化やdispatchで例外が発生してもrejectに必ず到達させる。
      // ここでrejectを取りこぼすとPromiseが未解決のままになり、
      // 呼び出し元のローディング状態が永久に解除されなくなる。
      try {
        // reducer側でerror.data / error.statusを直接参照しても例外にならないようにする。
        // 補完したresponseはstatusがnullなので、下のHTTPステータス別ディスパッチには入らない。
        normalizeErrorResponse(error);

        // ログインユーザーの切替前に発行されたリクエストは、旧セッションで処理されて401等になりうる。
        // その結果でサインイン画面に遷移させたり、ログイン状態を落としたりしない
        const isStaleSessionRequest = requestSessionGeneration !== sessionGeneration;

        if (!isStaleSessionRequest && error && error.response && error.response.status) {
          switch (error.response.status) {
            case HTTP_400_BAD_REQUEST_CODE:
              dispatch(badRequest(error));
              break;
            case HTTP_401_UNAUTHORIZED_CODE:
              dispatch(unauthorized(error));
              dispatch(push(`/keyman-letter-signin`));
              break;
            case HTTP_404_NOT_FOUND_CODE:
              dispatch(notFound(error));
              break;
            case HTTP_405_METHOD_NOT_ALLOWED_CODE:
              dispatch(methodNotAllowed(error));
              break;
            case HTTP_500_INTERNAL_SERVER_ERROR_CODE:
              dispatch(internalServerError(error));
              break;
            default:
              break;
          }
        }
      } catch (e) {
        console.log(e); // eslint-disable-line no-console
      }

      return reject(error);
    })
  })
}



// WEBPACK FOOTER //
// ./src/redux/api/ApiClient.js